import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";
import { useDispatch } from "react-redux";
import { Modal, Button, Form, Alert, Spinner } from "react-bootstrap";
import Select from "react-select";
import { savOperations } from "state/ducks/store/sav";
import { orderOperations } from "state/ducks/store/order";
import ResponseCard from "../Common/ResponseCard";
import SendHistoryList from "../Common/SendHistoryList";
import askDelayAndConfirm from "../Common/askDelayAndConfirm";

const ACTION_LABELS = {
  sms:   "Envoyer un SMS",
  email: "Envoyer un email",
};

/**
 * Modale d'action sur les points de retrait sélectionnés :
 * récupère les commandes du jour pour ces points puis envoie
 * un SMS ou un email aux clients concernés.
 */
const ActionModal = (props) => {

  const { action, date, selectedSpots, availableSpots, onChangeSelectedSpots, onClose, prefillContent } = props;

  const dispatch = useDispatch();

  const [orders, setOrders]           = useState([]);
  const [isTruncated, setIsTruncated] = useState(false);
  const [totalCount, setTotalCount]   = useState(0);
  const [isLoading, setIsLoading]     = useState(false);
  const [isSending, setIsSending]     = useState(false);
  const [response, setResponse]       = useState(false); // {'total', 'success', 'fails'}

  const [content, setContent] = useState("");
  const [historyRefresh, setHistoryRefresh] = useState(0);
  const [historyItems, setHistoryItems]     = useState([]);

  // Clients des points sélectionnés ayant déjà reçu un SMS/email aujourd'hui
  const notifiedEmails = historyItems
    .map((item) => item.customer && item.customer.email)
    .filter(Boolean);
  const alreadyNotified = orders.filter((order) =>
    order.customer_info && notifiedEmails.includes(order.customer_info.email)
  );

  // Clé stable de la sélection pour recharger les commandes quand elle change
  const spotIdsKey = selectedSpots.map((spot) => spot.spot_id).sort().join(",");

  // À l'ouverture : reset + pré-remplissage du SMS avec le gabarit retard
  // (le numéro d'intendance de la charrette de chaque client est ajouté
  // dynamiquement par l'API à l'envoi)
  useEffect(() => {
    if (!action) {
      return;
    }
    setResponse(false);
    if (action === "sms") {
      setContent(prefillContent
        || "Votre livraison a du retard (A RENSEIGNER). Toutes nos excuses. Nouveau créneau : A RENSEIGNER . Trop tard pour vous ?");
    } else {
      setContent("");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [action]);

  // Récupère les commandes du jour pour les points sélectionnés
  // (rechargé quand la sélection change, sans toucher au message en cours)
  useEffect(() => {
    if (!action) {
      return;
    }

    // Sans point sélectionné : pas de recherche de commandes (l'envoi est bloqué),
    // la popup sert alors à consulter l'historique ou à choisir des points
    if (selectedSpots.length === 0) {
      setOrders([]);
      setIsTruncated(false);
      setTotalCount(0);
      setIsLoading(false);
      return;
    }

    setIsLoading(true);

    const params = {
      deliveryType: "spot",
      startDate: date,
      endDate: date,
      spots: selectedSpots.map((spot) => spot.spot_id),
      groups: ["order_info", "order_multiple"],
    };

    dispatch(orderOperations.getOrders(params))
      .then((result) => {
        if (result?.data?.result?.orders) {
          setOrders(result.data.result.orders);
          setIsTruncated(result.data.result.isTruncated || false);
          setTotalCount(result.data.result.totalCount || 0);
        } else {
          setOrders([]);
          setIsTruncated(false);
          setTotalCount(0);
        }
      })
      .catch(() => {
        setOrders([]);
      })
      .finally(() => {
        setIsLoading(false);
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [action, date, spotIdsKey, dispatch]);

  const canSubmit = () => {
    if (isLoading || isSending || orders.length === 0) {
      return false;
    }
    if (action === "sms" || action === "email") {
      return content.trim().length > 0;
    }
    return false;
  };

  const handleSubmit = () => {
    const orderIds = orders.map((order) => order.id);

    let confirmText = "";
    if (action === "sms") {
      confirmText = `Vous allez envoyer un SMS aux clients de ${orderIds.length} commande(s).`;
    } else {
      confirmText = `Vous allez envoyer un email aux clients de ${orderIds.length} commande(s).`;
    }

    // Confirmation + « ce message annonce-t-il un retard ? » (Oui / Non).
    askDelayAndConfirm(confirmText, action).then((isDelay) => {
      if (isDelay === null) {
        return;
      }
      setIsSending(true);

      let operation;
      if (action === "sms") {
        operation = dispatch(savOperations.sendSMS(orderIds, null, null, null, content, true, isDelay));
      } else {
        operation = dispatch(savOperations.sendEmail(orderIds, content, isDelay));
      }

      operation
        .then((result) => {
          if (result?.data?.result) {
            setResponse(result.data.result);
          }
          setHistoryRefresh((key) => key + 1);
        })
        .catch(() => {
        })
        .finally(() => {
          setIsSending(false);
        });
    });
  };

  return (
    <Modal show={!!action} onHide={onClose} size="lg">
      <Modal.Header closeButton>
        <Modal.Title>{action ? ACTION_LABELS[action] : ""}</Modal.Title>
      </Modal.Header>
      <Modal.Body>

        {(action === "sms" || action === "email") && alreadyNotified.length > 0 && (
          <Alert variant="danger">
            <span role="img" aria-label="attention">⚠️</span>{" "}
            <strong>{alreadyNotified.length} client(s)</strong> des points sélectionnés{" "}
            {alreadyNotified.length > 1 ? "ont" : "a"} déjà reçu un SMS ou un email aujourd'hui —
            vérifiez l'historique en bas avant de renvoyer.
          </Alert>
        )}

        <Form.Group className="mb-2">
          <Form.Label>Points de retrait ciblés ({selectedSpots.length})</Form.Label>
          <Select
            isMulti
            options={availableSpots.map((spot) => ({
              value: spot.spot_id,
              label: `${spot.spot_name}${spot.tourName ? ` — ${spot.tourName}` : ""}`,
            }))}
            value={selectedSpots.map((spot) => ({
              value: spot.spot_id,
              label: spot.spot_name,
            }))}
            onChange={(options) => {
              const ids = (options || []).map((option) => option.value);
              onChangeSelectedSpots(availableSpots.filter((spot) => ids.includes(spot.spot_id)));
            }}
            placeholder="Sélectionner des points de retrait..."
            noOptionsMessage={() => "Aucun point trouvé"}
            menuPortalTarget={document.body}
            styles={{ menuPortal: (base) => ({ ...base, zIndex: 2000 }) }}
          />
        </Form.Group>

        {isLoading ? (
          <p>
            <Spinner animation="border" size="sm" className="mr-2" />
            Recherche des commandes du {date}...
          </p>
        ) : (
          <>
            <Alert variant={orders.length > 0 ? "info" : "warning"}>
              {selectedSpots.length === 0
                ? "Aucun point sélectionné — l'envoi est désactivé. Choisissez des points ci-dessus (ou sur la carte) pour envoyer un message ; l'historique du jour reste consultable ci-dessous."
                : orders.length > 0
                  ? `${orders.length} commande(s) trouvée(s) pour ces points le ${date}.`
                  : `Aucune commande trouvée pour ces points le ${date}.`}
              {isTruncated &&
                ` Attention : la liste est tronquée (${totalCount} commandes au total), toutes ne seront pas traitées.`}
            </Alert>

            {(action === "sms" || action === "email") && (
              <Form.Group>
                <Form.Label>
                  {action === "sms" ? "Contenu du SMS" : "Contenu de l'email"}
                </Form.Label>
                <Form.Control
                  as="textarea"
                  rows={action === "sms" ? 4 : 8}
                  value={content}
                  onChange={(e) => setContent(e.target.value)}
                  placeholder={action === "sms"
                    ? "Votre message... (envoyé tel quel par SMS)"
                    : "Votre message... (envoyé dans le gabarit email Le Moulin)"}
                />
                {action === "sms" && (
                  <Form.Text muted>
                    {content.length} caractère(s) — au-delà de 160 caractères, plusieurs SMS seront facturés.
                    <span className="d-block">
                      <span role="img" aria-label="info">ℹ️</span>{" "}
                      Le <strong>numéro d'intendance de la charrette du client</strong> sera
                      ajouté automatiquement à la fin du SMS (le bon numéro pour chaque client).
                    </span>
                  </Form.Text>
                )}
              </Form.Group>
            )}

          </>
        )}

        {response && (
          <ResponseCard
            title={`Total de ${response.total} client(s) traité(s)`}
            success={response.success || []}
            fails={response.fails || []}
          />
        )}

        {(action === "sms" || action === "email") && (
          <>
            <hr />
            <h6>Envois du jour (tous points confondus)</h6>
            <SendHistoryList date={date} refreshKey={historyRefresh} onLoaded={setHistoryItems} />
          </>
        )}

      </Modal.Body>
      <Modal.Footer>
        <Button variant="outline-secondary" onClick={onClose}>
          Fermer
        </Button>
        <Button variant="primary" onClick={handleSubmit} disabled={!canSubmit()}>
          {isSending ? (
            <>
              <Spinner animation="border" size="sm" className="mr-2" />
              Envoi en cours...
            </>
          ) : (
            action ? ACTION_LABELS[action] : ""
          )}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

ActionModal.propTypes = {
  action: PropTypes.oneOf(["sms", "email"]),
  date: PropTypes.string.isRequired,
  selectedSpots: PropTypes.array,
  availableSpots: PropTypes.array,
  onChangeSelectedSpots: PropTypes.func,
  prefillContent: PropTypes.string,
  onClose: PropTypes.func.isRequired,
};

ActionModal.defaultProps = {
  action: null,
  selectedSpots: [],
  availableSpots: [],
  onChangeSelectedSpots: () => {},
  prefillContent: null,
};

export default ActionModal;
